import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { 
  UserCheck, 
  Search, 
  Building2,
  Heart,
  Activity,
  TrendingUp,
  Eye,
  Download
} from 'lucide-react';
import { SystemAdminPatientAPI } from '../../../services/api/SystemAdminPatientAPI';

const GlobalPatientOverview = () => {
  const [patients, setPatients] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [statistics, setStatistics] = useState({
    total_patients: 0,
    active_patients: 0,
    inactive_patients: 0,
    total_recordings: 0,
    completed_recordings: 0,
    completion_rate: 0
  });
  const [loading, setLoading] = useState(false);
  const [statisticsError, setStatisticsError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTenant, setFilterTenant] = useState('');
  const [showingResults, setShowingResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    loadTenants();
    loadStatistics();
  }, []);

  // Only reload statistics when filters change
  useEffect(() => {
    if (searchTerm || filterTenant) {
      loadStatistics();
    }
  }, [searchTerm, filterTenant]);

  const loadTenants = async () => {
    try {
      const tenantsData = await SystemAdminPatientAPI.getAllTenants();
      console.log('Raw tenants data:', tenantsData);
      // Ensure tenants is always an array
      if (Array.isArray(tenantsData)) {
        setTenants(tenantsData);
      } else if (tenantsData && Array.isArray(tenantsData.tenants)) {
        setTenants(tenantsData.tenants);
      } else if (tenantsData && tenantsData.success && Array.isArray(tenantsData.tenants)) {
        setTenants(tenantsData.tenants);
      } else {
        console.warn('Unexpected tenants data format:', tenantsData);
        setTenants([]);
      }
    } catch (error) {
      toast.error('Failed to load tenants');
      console.error('Error loading tenants:', error);
      setTenants([]); // Ensure tenants is always an array even on error
    }
  };

  const loadStatistics = async () => {
    try {
      console.log('Loading patient statistics...');
      const stats = await SystemAdminPatientAPI.getPatientStatistics(searchTerm, filterTenant);
      console.log('Patient statistics loaded:', stats);
      setStatistics(stats);
      setStatisticsError(false);
    } catch (error) {
      console.error('Error loading patient statistics:', error);
      toast.error('Failed to load patient statistics');
      setStatisticsError(true);
      // Set error state instead of wrong data
      setStatistics({
        total_patients: 0,
        active_patients: 0,
        inactive_patients: 0,
        total_recordings: 0,
        completed_recordings: 0,
        completion_rate: 0
      });
    }
  };

  const loadPatients = async () => {
    try {
      setLoading(true);
      console.log('Loading patients with filters:', { searchTerm, filterTenant });
      
      const response = await SystemAdminPatientAPI.getAllPatients(
        1, // page 1
        1000, // large page size to get all results
        searchTerm,
        filterTenant
      );
      
      console.log('Patients loaded:', response.patients?.length || 0);
      setPatients(response.patients || []);
      setShowingResults(true);
      
    } catch (error) {
      toast.error('Failed to load patient data');
      console.error('Error loading patients:', error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm && !filterTenant) {
      toast.error('Please enter a search term or select a tenant filter');
      return;
    }
    
    setSearchLoading(true);
    await Promise.all([loadPatients(), loadStatistics()]);
    setSearchLoading(false);
  };

  const handleFilterChange = (newTenant) => {
    setFilterTenant(newTenant);
    if (newTenant) {
      // Auto-search when tenant is selected
      setSearchTerm('');
      setTimeout(() => handleSearch(), 100);
    } else {
      // Clear results when no tenant selected
      setPatients([]);
      setShowingResults(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterTenant('');
    setPatients([]);
    setShowingResults(false);
    loadStatistics(); // Reload overall statistics
  };

  // Statistics are now loaded separately from the API

  if (loading && patients.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Global Patient Overview</h1>
          <p className="text-gray-600">Monitor patients across all healthcare organizations</p>
        </div>
        <button className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" />
          <span>Export Data</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Patients</p>
              <p className="text-3xl font-bold text-gray-900">
                {statisticsError ? (
                  <span className="text-red-500">Error</span>
                ) : (
                  statistics.total_patients
                )}
              </p>
              {statisticsError && (
                <p className="text-xs text-red-500 mt-1">Unable to load data</p>
              )}
            </div>
            <div className="p-3 bg-[#DCEAF5] rounded-full">
              <UserCheck className="h-8 w-8 text-[#207CE5]" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Patients</p>
              <p className="text-3xl font-bold text-gray-900">
                {statisticsError ? (
                  <span className="text-red-500">Error</span>
                ) : (
                  statistics.active_patients
                )}
              </p>
              {statisticsError && (
                <p className="text-xs text-red-500 mt-1">Unable to load data</p>
              )}
            </div>
            <div className="p-3 bg-[#DCEAF5] rounded-full">
              <Activity className="h-8 w-8 text-[#008080]" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Recordings</p>
              <p className="text-3xl font-bold text-gray-900">
                {statisticsError ? (
                  <span className="text-red-500">Error</span>
                ) : (
                  statistics.total_recordings
                )}
              </p>
              {statisticsError && (
                <p className="text-xs text-red-500 mt-1">Unable to load data</p>
              )}
            </div>
            <div className="p-3 bg-[#DCEAF5] rounded-full">
              <Heart className="h-8 w-8 text-[#00BCD4]" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed Recordings</p>
              <p className="text-3xl font-bold text-gray-900">
                {statisticsError ? (
                  <span className="text-red-500">Error</span>
                ) : (
                  statistics.completed_recordings
                )}
              </p>
              <div className="flex items-center mt-1">
                <span className="text-sm text-gray-500">
                  {statisticsError ? (
                    <span className="text-red-500">Unable to load completion rate</span>
                  ) : (
                    `${statistics.completion_rate.toFixed(1)}% completion rate`
                  )}
                </span>
              </div>
            </div>
            <div className="p-3 bg-[#DCEAF5] rounded-full">
              <TrendingUp className="h-8 w-8 text-[#4682B4]" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Search & Filter</h3>
          <div className="flex items-center space-x-2">
            {showingResults && (
              <span className="text-sm text-gray-500">
                Showing {patients.length} patients
              </span>
            )}
            {(searchTerm || filterTenant) && (
              <button
                onClick={handleClearFilters}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Organization</label>
            <select
              value={filterTenant}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            >
              <option value="">All Organizations</option>
              {Array.isArray(tenants) && tenants.map(tenant => (
                <option key={tenant.id || tenant} value={tenant.id || tenant}>{tenant.name || tenant}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleSearch}
            disabled={searchLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#0B1C2C] disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            <span>{searchLoading ? 'Searching...' : 'Search'}</span>
          </button>
        </div>
      </div>

      {/* Patient Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {showingResults ? `Patient Records (${patients.length})` : 'Patient Records'}
          </h3>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading patients...</p>
          </div>
        ) : !showingResults ? (
          <div className="p-6 text-center text-gray-500">
            <UserCheck className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select a filter to view patients</h3>
            <p className="text-sm text-gray-500">
              Please enter a search term or select a tenant to view patient records.
            </p>
          </div>
        ) : patients.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <UserCheck className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No patients found</h3>
            <p className="text-sm text-gray-500">
              No patients match your current search or filter criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recordings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Recording
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {patients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#207CE5] to-[#00BCD4] flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {patient.alias.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                                                  <div className="text-sm font-medium text-gray-900">{patient.alias}</div>
                        <div className="text-sm text-gray-500">{patient.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{patient.tenant_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        patient.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {patient.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        Total: {patient.total_recordings || 0}
                      </div>
                      <div className="text-sm text-gray-500">
                        Completed: {patient.completed_recordings || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.last_recording ? new Date(patient.last_recording).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        className="text-[#207CE5] hover:text-[#0B1C2C]"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalPatientOverview;